import { cloneDeep, get, set } from "lodash";
import { getLocalstorageToken } from "src/context/AuthHelper";
import {
  KOSTEN_ARTEN,
  KOSTEN_POSITION,
  KOSTENTRAEGER,
  NACHWEIS_POSITION_STATUS,
} from "src/lib/constants/constant";
import MittelabrufSchema from "src/lib/paths/mittelabruf_FULL_Paths.json";
import NachweisPositionSchema from "src/lib/paths/nachweisposition_FULL_Paths.json";
import { addIndexesToPath } from "src/utils/helper";
import {
  getLoeweMittelabrufOrganizations,
  getMittelabrufPositionOrganizationId,
} from "src/pages/private/online-antrag/structure/mitelabruf/loewe/content/LoeweMittelabrufOrganizations";

const P = MittelabrufSchema.attributes;
const NP_P = NachweisPositionSchema.attributes;

export type LoeweMAFormulaResult = {
  path: string;
  value: any;
};

const toNumber = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const round = (value: number): number => Number(value.toFixed(2));

const getApplicantOrganizationId = (
  nachweispositionen: any[],
): number | string | undefined => {
  const applicantPosition = nachweispositionen.find(
    (position) =>
      get(position, NP_P.Allgemein_Kostentraeger.path) ===
      KOSTENTRAEGER.Antragsteller,
  );
  const applicantOrganizationId =
    getMittelabrufPositionOrganizationId(applicantPosition);
  return (
    applicantOrganizationId ?? get(getLocalstorageToken(), "organisationId")
  );
};

const getFundingRows = (application: any, organization: any): any[] => {
  if (organization.kostentraeger === KOSTENTRAEGER.Antragsteller) {
    return get(
      application,
      P.Foerderantrag.schema.attributes
        .Antragstellende_Organisation_Foerderquote.path,
      [],
    );
  }

  const partner = get(
    application,
    P.Foerderantrag.schema.attributes.Verbundpartner.path,
    [],
  ).find(
    (entry: any) =>
      String(get(entry, "Verbundpartner.id")) === String(organization.id),
  );
  return get(partner, "Foerderquote", []);
};

const getFundingRate = (
  application: any,
  organization: any,
  year: number,
  existingRows: any[],
): number => {
  const fundingRow = getFundingRows(application, organization).find(
    (entry: any) => toNumber(get(entry, "Projektjahr")) === year,
  );
  if (fundingRow) return toNumber(get(fundingRow, "Foerderquote_403741"));

  const existingRow = existingRows.find(
    (entry: any) => get(entry, "Zelle_1") === organization.label,
  );
  return toNumber(get(existingRow, "Zelle_2"));
};

const getOrganizationExpenses = (
  nachweispositionen: any[],
  organization: any,
  status: string,
): number => {
  return nachweispositionen.reduce((sum, position) => {
    const organizationId = getMittelabrufPositionOrganizationId(position);
    const isApplicantFallback =
      organization.kostentraeger === KOSTENTRAEGER.Antragsteller &&
      organizationId === undefined &&
      get(position, NP_P.Allgemein_Kostentraeger.path) ===
        KOSTENTRAEGER.Antragsteller;
    const belongsToOrganization =
      (organizationId !== undefined &&
        String(organizationId) === String(organization.id)) ||
      isApplicantFallback;
    const isEligibleExpense =
      get(position, NP_P.Allgemein_Kostenart.path) ===
        KOSTEN_POSITION.Ausgabe &&
      [KOSTEN_ARTEN.Personalkosten, KOSTEN_ARTEN.Sachkosten].includes(
        get(position, NP_P.Allgemein_Kostenart_Finanzierung.path),
      );

    if (
      !belongsToOrganization ||
      !isEligibleExpense ||
      get(position, NP_P.Allgemein_Status.path) !== status
    )
      return sum;
    return sum + toNumber(get(position, NP_P.Allgemein_Ausgaben_Kosten.path));
  }, 0);
};

export const calculateLoewePersonalAusgaben = (
  application: any,
): LoeweMAFormulaResult[] => {
  const nachweispositionen = get(
    application,
    P.Mittelabruf_Nachweispositions.path,
    [],
  );
  const ausgabenPath =
    P.Mittelabruf_Nachweispositions.schema.attributes.Allgemein_Ausgaben_Kosten
      .path;

  return nachweispositionen.reduce(
    (result: LoeweMAFormulaResult[], position: any, index: number) => {
      if (
        get(position, NP_P.Allgemein_Kostenart_Finanzierung.path) !==
        KOSTEN_ARTEN.Personalkosten
      )
        return result;

      const hours =
        Number(get(position, NP_P.Stundennachweis_Gesamtstunden.path, 0)) || 0;
      const hourlyRate =
        Number(get(position, NP_P.Stundennachweis_Stundensatz.path, 0)) || 0;
      const eligibleExpenses = hours * hourlyRate;

      result.push({
        path: addIndexesToPath(
          ausgabenPath,
          [`${index}`],
          ["Mittelabruf_Nachweispositions"],
        ),
        value: eligibleExpenses.toFixed(2),
      });
      return result;
    },
    [],
  );
};

export const calculateLoeweMittelabruf = (
  application: any,
): LoeweMAFormulaResult[] => {
  const nachweispositionen = get(
    application,
    P.Mittelabruf_Nachweispositions.path,
    [],
  );
  const existingRows = get(application, P.Kalkulation_Block_1.path, []);
  const applicantOrganizationId =
    getApplicantOrganizationId(nachweispositionen);
  const organizations = getLoeweMittelabrufOrganizations(
    application,
    applicantOrganizationId,
  );
  const year = toNumber(get(application, P.Allgemein_Haushaltsjahr.path));
  const rows = organizations.map((organization) => {
    const actual = getOrganizationExpenses(
      nachweispositionen,
      organization,
      NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben,
    );
    const planned = getOrganizationExpenses(
      nachweispositionen,
      organization,
      NACHWEIS_POSITION_STATUS.geplant,
    );
    const total = actual + planned;
    const fundingRate = getFundingRate(
      application,
      organization,
      year,
      existingRows,
    );
    const existingRow = existingRows.find(
      (entry: any) => get(entry, "Zelle_1") === organization.label,
    );

    return {
      ...(get(existingRow, "id") ? { id: get(existingRow, "id") } : {}),
      Zelle_1: organization.label,
      Zelle_2: round(fundingRate),
      Zelle_3: round(actual),
      Zelle_4: round((total * fundingRate) / 100),
      Zelle_5: round(planned),
      Zelle_6: round(total),
    };
  });

  const actualTotal = rows.reduce((sum, row) => sum + toNumber(row.Zelle_3), 0);
  const plannedTotal = rows.reduce(
    (sum, row) => sum + toNumber(row.Zelle_5),
    0,
  );
  const expensesTotal = rows.reduce(
    (sum, row) => sum + toNumber(row.Zelle_6),
    0,
  );
  const fundingTotal = rows.reduce(
    (sum, row) => sum + toNumber(row.Zelle_4),
    0,
  );
  const alreadyPaid = toNumber(
    get(
      application,
      P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path,
    ),
  );
  const approvedFunding = toNumber(
    get(
      application,
      P.Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung.path,
    ),
  );
  const availableFunding = Math.max(0, approvedFunding - alreadyPaid);
  const maximumRequest = Math.max(
    0,
    Math.min(fundingTotal - alreadyPaid, availableFunding),
  );
  const individualFundingRate =
    expensesTotal > 0 ? (fundingTotal / expensesTotal) * 100 : 0;

  return [
    { path: P.Kalkulation_Block_1.path, value: rows },
    {
      path: P.Individuelle_Foerderquote_Gesamt.path,
      value: round(individualFundingRate).toFixed(2),
    },
    {
      path: P.Tatsaechlich_Getaetigte_Ausgaben_Gesamt.path,
      value: round(actualTotal).toFixed(2),
    },
    {
      path: P.Kalkulation_Foerderquote_47_Ausgaben_Block_2_Zelle_5.path,
      value: round(plannedTotal).toFixed(2),
    },
    {
      path: P.Kalkulation_Foerderquote_47_Ausgaben_Block_2_Zelle_6.path,
      value: round(expensesTotal).toFixed(2),
    },
    {
      path: P.Zuwendung_Anteilig_Gesamt.path,
      value: round(fundingTotal).toFixed(2),
    },
    {
      path: P.Ermittlung_Des_Auszahlungsbetrages_Zuwendungsfaehige_Ausgaben
        .path,
      value: round(expensesTotal).toFixed(2),
    },
    {
      path: P.Ermittlung_Des_Auszahlungsbetrages_Zuwendung_Anteilig.path,
      value: round(fundingTotal).toFixed(2),
    },
    {
      path: P.Ermittlung_Des_Auszahlungsbetrages_Anforderungsbetrag.path,
      value: round(maximumRequest).toFixed(2),
    },
  ];
};

export const executeLoeweMAFormulas = (
  application: any,
): LoeweMAFormulaResult[] => {
  const personalResults = calculateLoewePersonalAusgaben(application);
  const applicationWithPersonalExpenses = cloneDeep(application);
  personalResults.forEach((result) =>
    set(applicationWithPersonalExpenses, result.path, result.value),
  );
  return [
    ...personalResults,
    ...calculateLoeweMittelabruf(applicationWithPersonalExpenses),
  ];
};
